import React, { useEffect, useState, useCallback } from "react";
import MainLayout from "../components/layout/MainLayout";
import CopyAuditHub from "../components/CopyAuditHub";
import { API_BASE_URL } from "../config";

// ─── Types ──────────────────────────────────────────────────────────────

interface LibraryCampaign {
  campaignId: string;
  name: string;
  brandName?: string;
  advertiser: string;
  geo: string;
  category: string;
  landingPageUrl?: string;
  affiliateLinkSlug?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TextVersion {
  campaignId: string;
  versionId: string;
  subject: string;
  previewText: string;
  subjectPairs?: Array<{ subject: string; previewText: string }>;
  lastUsedSubjectIndex?: number;
  textContent: string;
  disclaimer: string;
  status: string;
  sourceEsp?: string;
  sourceName?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AffLinkOption {
  slug: string;
  name: string;
  geo: string;
  category: string;
  is_active: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────

const GEOS = [
  { id: "NO", label: "Norway", flag: "no" },
  { id: "SE", label: "Sweden", flag: "se" },
  { id: "FI", label: "Finland", flag: "fi" },
  { id: "DE", label: "Germany", flag: "de" },
];

// ─── Component ──────────────────────────────────────────────────────────

export default function TextLibrary() {
  const [selectedGeo, setSelectedGeo] = useState("NO");
  const [libCampaigns, setLibCampaigns] = useState<LibraryCampaign[]>([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libSearch, setLibSearch] = useState('');
  const [selectedLibCampaign, setSelectedLibCampaign] = useState<LibraryCampaign | null>(null);
  const [libVersions, setLibVersions] = useState<TextVersion[]>([]);
  const [editingVersion, setEditingVersion] = useState<TextVersion | null>(null);
  const [libSaveMsg, setLibSaveMsg] = useState('');
  const [libPreview, setLibPreview] = useState('');
  const [previewEspId, setPreviewEspId] = useState('fb_se');
  const [rightPanelTab, setRightPanelTab] = useState<'studio' | 'chat'>('studio');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [affiliateLinks, setAffiliateLinks] = useState<AffLinkOption[]>([]);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState<{role: 'user' | 'assistant'; content: string; displayContent?: string; hasUpdate?: boolean; rawText?: string; rawSubject?: string; rawPreview?: string}[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Subject generator
  const [subjectSuggestions, setSubjectSuggestions] = useState<{subject: string; preview: string}[]>([]);
  const [subjectGenLoading, setSubjectGenLoading] = useState(false);

  // 1-Click Complete Draft state
  const [completeDraftLoading, setCompleteDraftLoading] = useState(false);
  const [selectedAngle, setSelectedAngle] = useState<'standard' | 'refinance' | 'speed' | 'short'>('standard');

  // ─── Render Live ESP Preview ───────────────────────────────────

  const renderEspPreview = useCallback(async (targetEspId?: string) => {
    if (!editingVersion || !editingVersion.textContent) return;
    const esp = targetEspId || previewEspId;
    setPreviewLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/text-library/render-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          textContent: editingVersion.textContent || '',
          previewText: editingVersion.previewText || '',
          disclaimer: editingVersion.disclaimer || '',
          espId: esp,
          geo: selectedLibCampaign?.geo || selectedGeo,
          campaignId: selectedLibCampaign?.campaignId || '',
          versionId: editingVersion.versionId || '',
        }),
      });
      const d = await r.json();
      if (d.error) {
        setLibPreview(`<div style="padding:24px;color:#dc2626;font-family:sans-serif;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;"><strong>⚠️ Link / Template Error</strong><br/>${d.error}</div>`);
      } else {
        setLibPreview(d.html || '');
      }
    } catch (e: any) {
      setLibPreview(`<div style="padding:24px;color:#dc2626;font-family:sans-serif;">Error rendering preview: ${e.message}</div>`);
    } finally {
      setPreviewLoading(false);
    }
  }, [editingVersion, previewEspId, selectedLibCampaign, selectedGeo]);

  // ─── Load data ────────────────────────────────────────────────

  const loadCampaigns = useCallback(async (geo: string) => {
    setLibLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/text-library/campaigns?geo=${geo}`);
      const d = await r.json();
      setLibCampaigns(d.campaigns || []);
    } catch (e) {
      console.error(e);
    }
    setLibLoading(false);
  }, []);

  const loadAffiliateLinks = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/affiliate/links`);
      const d = await r.json();
      setAffiliateLinks((d.data || d) || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadCampaigns(selectedGeo);
    loadAffiliateLinks();
    if (selectedGeo === 'SE') setPreviewEspId('fb_se');
    else if (selectedGeo === 'NO') setPreviewEspId('gr_no');
    else if (selectedGeo === 'FI') setPreviewEspId('ml_fi');
    else setPreviewEspId('fb_de');
  }, [selectedGeo, loadCampaigns, loadAffiliateLinks]);

  // Auto-render preview when opening a version or switching ESP tabs
  useEffect(() => {
    if (editingVersion && editingVersion.textContent && rightPanelTab === 'studio') {
      renderEspPreview();
    }
  }, [editingVersion?.versionId, previewEspId, rightPanelTab]);

  const [activeHubTab, setActiveHubTab] = useState<'audit' | 'studio'>('audit');

  const selectCampaign = async (c: LibraryCampaign, targetVersionId?: string) => {
    setSelectedLibCampaign(c);
    setEditingVersion(null);
    setLibPreview('');
    const defaultEsp = c.geo === 'SE' ? 'fb_se' : c.geo === 'NO' ? 'gr_no' : c.geo === 'FI' ? 'ml_fi' : 'fb_de';
    setPreviewEspId(defaultEsp);
    try {
      const r = await fetch(`${API_BASE_URL}/api/text-library/versions?campaignId=${c.campaignId}`);
      const d = await r.json();
      const vers = d.versions || [];
      setLibVersions(vers);
      if (targetVersionId) {
        const match = vers.find((v: any) => v.versionId === targetVersionId);
        if (match) {
          setEditingVersion({ ...match });
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenInStudio = async (campaignId: string, versionId?: string) => {
    setActiveHubTab('studio');
    let camp = libCampaigns.find(c => c.campaignId === campaignId);
    if (!camp) {
      try {
        const r = await fetch(`${API_BASE_URL}/api/text-library/campaigns?geo=${selectedGeo}`);
        const d = await r.json();
        const freshList = d.campaigns || [];
        setLibCampaigns(freshList);
        camp = freshList.find((c: any) => c.campaignId === campaignId);
      } catch (e) {
        console.error(e);
      }
    }
    if (camp) {
      await selectCampaign(camp, versionId);
    }
  };

  // ─── Render ───────────────────────────────────────────────────

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in pb-12">
        {/* Header with Navigation Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-200 pb-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                📚 Text Library
              </h2>
              <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm">
                4-GEO Master Hub
              </span>
            </div>
            <p className="text-gray-500 mt-1 text-sm">
              Audit, manually approve golden copies, and author campaign texts across SE, NO, FI, and DE
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Tabs */}
            <div className="inline-flex bg-gray-100 p-1 rounded-xl border border-gray-200 shadow-inner">
              <button
                type="button"
                onClick={() => setActiveHubTab('audit')}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  activeHubTab === 'audit'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>🛡️</span>
                <span>Copy Audit & Approval</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveHubTab('studio')}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  activeHubTab === 'studio'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>✍️</span>
                <span>Studio & Editor</span>
              </button>
            </div>

            {/* GEO selector for studio mode */}
            {activeHubTab === 'studio' && (
              <select
                value={selectedGeo}
                onChange={(e) => setSelectedGeo(e.target.value)}
                className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg px-3 py-1.5 focus:ring-indigo-500 font-medium"
              >
                {GEOS.map(g => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {activeHubTab === 'audit' ? (
          <CopyAuditHub
            selectedGeo={selectedGeo}
            setSelectedGeo={setSelectedGeo}
            onOpenInStudio={handleOpenInStudio}
          />
        ) : (
          /* Main Grid */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Campaign List */}
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={libSearch}
                onChange={e => setLibSearch(e.target.value)}
                placeholder="Search campaigns..."
                className="flex-1 text-sm rounded-lg px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <button
                onClick={() => loadCampaigns(selectedGeo)}
                className="px-3 py-2 rounded-lg text-sm border border-gray-200 hover:bg-gray-50"
              >
                🔄
              </button>
            </div>

            {libSaveMsg && (
              <div className="text-xs text-green-600 bg-green-50 rounded px-2 py-1">✅ {libSaveMsg}</div>
            )}

            {/* Campaign List */}
            {libLoading ? (
              <div className="text-sm text-gray-400 text-center py-8">Loading...</div>
            ) : libCampaigns.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-8">
                No campaigns in library. Create campaigns in Campaign Hub first.
              </div>
            ) : (
              <div className="space-y-1">
                {libCampaigns
                  .filter(c => !libSearch || c.name.toLowerCase().includes(libSearch.toLowerCase()))
                  .map(c => (
                    <button
                      key={c.campaignId}
                      onClick={() => selectCampaign(c)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        selectedLibCampaign?.campaignId === c.campaignId
                          ? 'bg-indigo-50 border-indigo-300 border font-medium'
                          : 'bg-white border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-gray-400">
                        {c.geo} · {c.category || 'uncategorized'}
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Middle: Version List */}
          <div className="space-y-4">
            {selectedLibCampaign ? (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">{selectedLibCampaign.name}</h2>
                  <span className="text-xs text-gray-400 bg-gray-100 rounded px-2 py-0.5">
                    {selectedLibCampaign.geo} · {selectedLibCampaign.category || '—'}
                  </span>
                </div>

                {/* Campaign Config */}
                <div className="space-y-1.5">
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-gray-400 whitespace-nowrap">🏷️ Brand Name:</span>
                    <input
                      type="text"
                      value={selectedLibCampaign.brandName || ''}
                      onChange={e => {
                        const updated = {...selectedLibCampaign, brandName: e.target.value};
                        setSelectedLibCampaign(updated);
                      }}
                      onBlur={async () => {
                        await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(selectedLibCampaign),
                        });
                      }}
                      className="flex-1 text-xs rounded px-2 py-1 border border-gray-200 bg-gray-50 font-medium text-gray-800 focus:bg-white"
                      placeholder="e.g. Sparklubben, Advisa, Lendo"
                    />
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-gray-400 whitespace-nowrap">🔗 Affiliate Link:</span>
                    <select
                      value={selectedLibCampaign.affiliateLinkSlug || ''}
                      onChange={async (e) => {
                        const slug = e.target.value;
                        const updated = {...selectedLibCampaign, affiliateLinkSlug: slug};
                        setSelectedLibCampaign(updated);
                        await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(updated),
                        });
                      }}
                      className="flex-1 text-xs rounded px-2 py-1 border border-gray-200 bg-gray-50"
                    >
                      <option value="">— Select affiliate link —</option>
                      {affiliateLinks.filter(l => l.is_active).map(l => (
                        <option key={l.slug} value={l.slug}>
                          /go/{l.slug}/ — {l.name} ({l.geo})
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedLibCampaign.affiliateLinkSlug && (
                    <div className="text-[10px] text-indigo-500 font-mono bg-indigo-50 rounded px-2 py-1">
                      CTA URL: https://afima.ai/go/{selectedLibCampaign.affiliateLinkSlug}/
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-gray-400 whitespace-nowrap">📄 Landing page (for AI):</span>
                    <input
                      type="text"
                      value={selectedLibCampaign.landingPageUrl || ''}
                      onChange={e => {
                        const updated = {...selectedLibCampaign, landingPageUrl: e.target.value};
                        setSelectedLibCampaign(updated);
                      }}
                      onBlur={async () => {
                        await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(selectedLibCampaign),
                        });
                      }}
                      className="flex-1 text-xs rounded px-2 py-1 border border-gray-200 bg-gray-50"
                      placeholder="https://ms.svarnorge.no/Product.aspx?productId=..."
                    />
                  </div>
                </div>

                {/* Description */}
                <textarea
                  value={selectedLibCampaign.description || ''}
                  onChange={e => {
                    const updated = {...selectedLibCampaign, description: e.target.value};
                    setSelectedLibCampaign(updated);
                  }}
                  onBlur={async () => {
                    await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(selectedLibCampaign),
                    });
                  }}
                  className="w-full text-xs rounded px-2 py-1.5 border border-gray-200 bg-gray-50 resize-none min-h-[50px]"
                  placeholder="Campaign description — what is this campaign about?..."
                />

                {libVersions.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-8">No text versions yet</div>
                ) : (
                  <div className="space-y-2">
                    {libVersions.map(v => (
                      <button
                        key={v.versionId}
                        onClick={() => setEditingVersion({...v})}
                        className={`w-full text-left rounded-lg border p-3 transition-all ${
                          editingVersion?.versionId === v.versionId
                            ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200'
                            : 'border-gray-200 bg-white hover:border-indigo-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-sm uppercase">{v.versionId}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            v.status === 'ready' ? 'bg-green-100 text-green-700' :
                            v.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            v.status === 'imported' ? 'bg-amber-100 text-amber-700' :
                            v.status === 'inactive' || v.status === 'archived' ? 'bg-rose-100 text-rose-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{v.status || 'draft'}</span>
                        </div>
                        <div className="text-sm font-medium text-gray-800 truncate">{v.subject || '(no subject)'}</div>
                        <div className="text-xs text-gray-500 truncate">{v.previewText || '(no preview)'}</div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Create new version */}
                <button
                  onClick={() => {
                    const maxNum = Math.max(0, ...libVersions.map(v => {
                      const m = v.versionId.match(/ta?(\d+)/i);
                      return m ? parseInt(m[1]) : 0;
                    }));
                    setEditingVersion({
                      campaignId: selectedLibCampaign.campaignId,
                      versionId: `t${maxNum + 1}`,
                      subject: '',
                      previewText: '',
                      textContent: '',
                      disclaimer: '',
                      status: 'draft',
                    });
                  }}
                  className="w-full px-3 py-2 rounded-lg text-sm font-medium border-2 border-dashed border-gray-300 text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                >
                  + Create New Text Version
                </button>
              </>
            ) : (
              <div className="text-sm text-gray-400 text-center py-12">← Select a campaign</div>
            )}
          </div>

          {/* Right: placeholder when no version selected */}
          {!editingVersion && (
            <div className="text-sm text-gray-400 text-center py-12">← Select a text version to edit</div>
          )}
        </div>
        )}

        {/* ═══ MODAL: Version Editor + AI Chat ═══ */}
        {editingVersion && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0,0,0,0.4)' }}>
            <div
              className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
              style={{ width: '92vw', height: '90vh', maxWidth: '1600px', animation: 'modalSlideUp 0.2s ease-out' }}
            >
              {/* ── Modal Header ─────────────────────────────── */}
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50/80">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-bold text-gray-800">{selectedLibCampaign?.name}</h2>
                  <span className="text-xs text-gray-400">·</span>
                  <span className="text-sm font-semibold text-indigo-600 uppercase">{editingVersion.versionId}</span>
                  <select
                    value={editingVersion.status || 'draft'}
                    onChange={e => setEditingVersion({...editingVersion, status: e.target.value})}
                    className="text-xs rounded-md border border-gray-200 px-2 py-1 bg-white ml-2 font-medium"
                  >
                    <option value="ready">✅ Ready (Active)</option>
                    <option value="draft">📝 Draft</option>
                    <option value="inactive">⏸️ Inactive (Archived)</option>
                    <option value="imported">📥 Imported</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {libSaveMsg && <span className="text-xs text-green-600 font-medium">{libSaveMsg}</span>}
                  <button
                    onClick={async () => {
                      await fetch(`${API_BASE_URL}/api/text-library/version`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(editingVersion),
                      });
                      setLibSaveMsg('Saved!');
                      setTimeout(() => setLibSaveMsg(''), 2000);
                      if (selectedLibCampaign) {
                        const r = await fetch(`${API_BASE_URL}/api/text-library/versions?campaignId=${selectedLibCampaign.campaignId}`);
                        const d = await r.json();
                        setLibVersions(d.versions || []);
                      }
                    }}
                    className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  >
                    💾 Save
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ${editingVersion.versionId}?`)) return;
                      await fetch(`${API_BASE_URL}/api/text-library/version?campaignId=${editingVersion.campaignId}&versionId=${editingVersion.versionId}`, { method: 'DELETE' });
                      setEditingVersion(null);
                      setChatMessages([]);
                      setLibPreview('');
                      if (selectedLibCampaign) {
                        const r = await fetch(`${API_BASE_URL}/api/text-library/versions?campaignId=${selectedLibCampaign.campaignId}`);
                        const d = await r.json();
                        setLibVersions(d.versions || []);
                      }
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 border border-red-200 transition-colors"
                  >
                    🗑
                  </button>
                  <button
                    onClick={() => { setEditingVersion(null); setChatMessages([]); setLibPreview(''); }}
                    className="ml-2 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors text-lg"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* ── Modal Body: Two panels ───────────────────── */}
              <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
                {/* ─── Left Panel: Text Editor ─────────────── */}
                <div className="flex flex-col gap-3 p-5 overflow-y-auto border-r border-gray-100">
                  
                  {/* ─── 1-Click Magic Full Draft Generator ─── */}
                  <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border border-purple-200/80 rounded-xl p-3 shadow-xs">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">✨</span>
                        <span className="text-xs font-bold text-purple-900">1-Click Magic Draft</span>
                        <span className="text-[10px] text-purple-600 font-medium">(Subject, Preview, Body, Disclaimer)</span>
                      </div>
                      <button
                        disabled={completeDraftLoading}
                        onClick={async () => {
                          if (!selectedLibCampaign) return;
                          setCompleteDraftLoading(true);
                          try {
                            const r = await fetch(`${API_BASE_URL}/api/text-library/generate-complete-draft`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                campaignName: selectedLibCampaign.name,
                                advertiser: selectedLibCampaign.advertiser,
                                geo: selectedLibCampaign.geo || selectedGeo,
                                angle: selectedAngle,
                                description: selectedLibCampaign.description || '',
                                landingPageUrl: selectedLibCampaign.landingPageUrl || '',
                              }),
                            });
                            const d = await r.json();
                            if (d.error) {
                              alert('Error: ' + d.error);
                            } else {
                              setEditingVersion({
                                ...editingVersion,
                                subject: d.subject || editingVersion.subject,
                                previewText: d.previewText || editingVersion.previewText,
                                textContent: d.textContent || editingVersion.textContent,
                                disclaimer: d.disclaimer || editingVersion.disclaimer,
                              });
                              if (d.allSubjects) {
                                setSubjectSuggestions(d.allSubjects);
                              }
                              setLibSaveMsg('✨ Complete draft generated!');
                              setTimeout(() => setLibSaveMsg(''), 2500);
                            }
                          } catch (e) {
                            console.error(e);
                          }
                          setCompleteDraftLoading(false);
                        }}
                        className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <span className={completeDraftLoading ? 'animate-spin' : ''}>⚡</span>
                        {completeDraftLoading ? 'Writing full mailer...' : 'Generate Full Version'}
                      </button>
                    </div>

                    {/* Angle selection pills */}
                    <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-purple-100">
                      <span className="text-[10px] font-bold text-purple-700 uppercase tracking-wider mr-1">Angle:</span>
                      {[
                        { id: 'standard', label: '🚀 Standard Comparison' },
                        { id: 'refinance', label: '💰 Samla lån & Spara' },
                        { id: 'speed', label: '⚡ Snabb 1-UC' },
                        { id: 'short', label: '🎯 Kort & Direkt' },
                      ].map(ang => (
                        <button
                          key={ang.id}
                          onClick={() => setSelectedAngle(ang.id as any)}
                          className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium transition-all ${
                            selectedAngle === ang.id
                              ? 'bg-purple-600 text-white font-semibold shadow-xs ring-1 ring-purple-400'
                              : 'bg-white text-purple-900 border border-purple-200 hover:bg-purple-100/60'
                          }`}
                        >
                          {ang.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 3-Pair Subject & Preview Rotation Selector */}
                  {editingVersion.subjectPairs && editingVersion.subjectPairs.length > 0 && (
                    <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-indigo-900 uppercase flex items-center gap-1.5">
                          <span>🔄</span> Rotating Subject & Preview Pairs (3 Active Cycles)
                        </label>
                        <span className="text-[10px] text-indigo-600 font-medium">Scheduler rotates automatically</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {editingVersion.subjectPairs.map((pair, idx) => {
                          const isSelected = editingVersion.subject === pair.subject;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setEditingVersion({
                                ...editingVersion,
                                subject: pair.subject,
                                previewText: pair.previewText
                              })}
                              className={`p-2 text-left rounded-lg border text-xs transition-all ${
                                isSelected
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm font-semibold'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                              }`}
                            >
                              <div className={`text-[10px] uppercase font-bold mb-0.5 ${isSelected ? 'text-indigo-100' : 'text-gray-400'}`}>
                                Pair #{idx + 1}
                              </div>
                              <div className="truncate font-medium">{pair.subject}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Subject + Preview */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Subject Line</label>
                      <input
                        type="text"
                        value={editingVersion.subject}
                        onChange={e => setEditingVersion({...editingVersion, subject: e.target.value})}
                        className="w-full text-sm rounded-lg px-3 py-2 border border-gray-200 bg-white focus:ring-2 focus:ring-indigo-400 focus:outline-none transition-shadow"
                        placeholder="Subject line..."
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Preview Text</label>
                      <input
                        type="text"
                        value={editingVersion.previewText}
                        onChange={e => setEditingVersion({...editingVersion, previewText: e.target.value})}
                        className="w-full text-sm rounded-lg px-3 py-2 border border-gray-200 bg-white focus:ring-2 focus:ring-indigo-400 focus:outline-none transition-shadow"
                        placeholder="Preview text..."
                      />
                    </div>
                  </div>

                  {/* Subject Line Generator */}
                  <div>
                    <button
                      disabled={subjectGenLoading}
                      onClick={async () => {
                        if (!selectedLibCampaign) return;
                        setSubjectGenLoading(true);
                        setSubjectSuggestions([]);
                        try {
                          const r = await fetch(`${API_BASE_URL}/api/text-library/generate-subjects`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              campaignName: selectedLibCampaign.name,
                              advertiser: selectedLibCampaign.advertiser,
                              geo: selectedLibCampaign.geo || selectedGeo,
                              bodyText: editingVersion.textContent || '',
                              description: selectedLibCampaign.description || '',
                            }),
                          });
                          const d = await r.json();
                          setSubjectSuggestions(d.suggestions || []);
                        } catch (e) { console.error(e); }
                        setSubjectGenLoading(false);
                      }}
                      className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 transition-colors"
                    >
                      {subjectGenLoading ? '✨ Generating...' : '✨ Generate Subject Lines · 3 Options'}
                    </button>
                    {subjectSuggestions.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {subjectSuggestions.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setEditingVersion({...editingVersion, subject: s.subject, previewText: s.preview});
                              setSubjectSuggestions([]);
                            }}
                            className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
                          >
                            <div className="text-sm font-medium text-gray-800 group-hover:text-indigo-700">
                              {i + 1}. {s.subject}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{s.preview}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Body text */}
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Email Body</label>
                      <span className="text-[9px] text-gray-400">{'→ CTA · **bold** · • bullet · {{name}}'}</span>
                    </div>
                    <textarea
                      value={editingVersion.textContent || ''}
                      onChange={e => setEditingVersion({...editingVersion, textContent: e.target.value})}
                      className="flex-1 w-full text-sm rounded-lg p-4 border border-gray-200 bg-white leading-relaxed resize-none focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono transition-shadow"
                      style={{ minHeight: '280px' }}
                      placeholder={
                        (selectedLibCampaign?.geo || selectedGeo).toUpperCase() === 'SE'
                          ? 'Hej {{name}},\n\nSkriv e-posttexten här...\n\n**Fördelar:**\n• Punkt 1\n• Punkt 2\n\n→ Jämför lån här'
                          : (selectedLibCampaign?.geo || selectedGeo).toUpperCase() === 'FI'
                          ? 'Hei {{name}},\n\nKirjoita teksti tähän...\n\n**Edut:**\n• Kohta 1\n• Kohta 2\n\n→ Hae tästä'
                          : (selectedLibCampaign?.geo || selectedGeo).toUpperCase() === 'DE'
                          ? 'Hallo {{name}},\n\nSchreiben Sie hier den Text...\n\n**Ihre Vorteile:**\n• Punkt 1\n• Punkt 2\n\n→ Jetzt vergleichen'
                          : 'Hei {{name}}.\n\nSkriv e-postteksten her...\n\n**Fordeler:**\n• Punkt 1\n• Punkt 2\n\n→ Søk her'
                      }
                    />
                  </div>

                  {/* Disclaimer */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Specific Loan Terms / Ränteexempel</label>
                      <button
                        onClick={async () => {
                          if (!selectedLibCampaign) return;
                          const r = await fetch(`${API_BASE_URL}/api/text-library/disclaimer`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ advertiser: selectedLibCampaign.advertiser, geo: selectedLibCampaign.geo || selectedGeo }),
                          });
                          const d = await r.json();
                          if (d.disclaimer) setEditingVersion({...editingVersion, disclaimer: d.disclaimer});
                        }}
                        className="text-[10px] text-purple-600 hover:text-purple-800 font-semibold"
                      >
                        ⚡ Auto-generate
                      </button>
                    </div>
                    <textarea
                      value={editingVersion.disclaimer || ''}
                      onChange={e => setEditingVersion({...editingVersion, disclaimer: e.target.value})}
                      className="w-full min-h-[70px] text-[13px] rounded-lg p-3 border border-gray-200 bg-gray-50 resize-none leading-relaxed"
                      placeholder="Ränteexempel / loan terms (Regulatory warning box & partner disclaimer are attached automatically)..."
                    />
                  </div>

                </div>

                {/* ─── Right Panel: Tabbed (ESP Studio & AI Chat) ─── */}
                <div className="flex flex-col overflow-hidden bg-gray-50/50">
                  {/* Top Tab Bar */}
                  <div className="px-5 py-2.5 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
                    <div className="flex items-center gap-1 bg-slate-800/80 p-1 rounded-lg">
                      <button
                        onClick={() => setRightPanelTab('studio')}
                        className={`px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
                          rightPanelTab === 'studio'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <span>⚡</span> Live ESP Studio
                      </button>
                      <button
                        onClick={() => setRightPanelTab('chat')}
                        className={`px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
                          rightPanelTab === 'chat'
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <span>✨</span> AI Assistant
                      </button>
                    </div>

                    {rightPanelTab === 'studio' && (
                      <div className="flex items-center gap-2">
                        {/* Device view toggle */}
                        <div className="flex items-center bg-slate-800 rounded-md p-0.5 text-[11px]">
                          <button
                            onClick={() => setPreviewDevice('desktop')}
                            className={`px-2 py-0.5 rounded ${previewDevice === 'desktop' ? 'bg-slate-700 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}
                            title="Desktop View (100%)"
                          >
                            💻 Desktop
                          </button>
                          <button
                            onClick={() => setPreviewDevice('mobile')}
                            className={`px-2 py-0.5 rounded ${previewDevice === 'mobile' ? 'bg-slate-700 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}
                            title="Mobile View (375px)"
                          >
                            📱 Mobile
                          </button>
                        </div>
                      </div>
                    )}

                    {rightPanelTab === 'chat' && (
                      <button onClick={() => setChatMessages([])} className="text-[10px] px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors font-medium">
                        Clear Chat
                      </button>
                    )}
                  </div>

                  {/* ════ TAB 1: LIVE ESP STUDIO ════ */}
                  {rightPanelTab === 'studio' ? (
                    <div className="flex-1 flex flex-col overflow-hidden bg-slate-100/70">
                      {/* ESP Toolbar */}
                      <div className="px-5 py-3 bg-white border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 shadow-sm">
                        {/* ESP Selector Pills */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mr-1">Target ESP:</span>
                          {[
                            { id: 'fb_se', label: 'FeedBlitz SE', flag: '🇸🇪', esp: 'FeedBlitz' },
                            { id: 'gr_se', label: 'GetResponse SE', flag: '🇸🇪', esp: 'GetResponse' },
                            { id: 'dp_se', label: 'Drip (Enkelt Lån)', flag: '🇸🇪', esp: 'Drip' },
                            { id: 'ms_se', label: 'Moosend SE', flag: '🇸🇪', esp: 'Moosend' },
                            { id: 'br_se', label: 'Brevo SE', flag: '🇸🇪', esp: 'Brevo' },
                            { id: 'gr_no', label: 'GetResponse NO', flag: '🇳🇴', esp: 'GetResponse' },
                            { id: 'fb_no', label: 'FeedBlitz NO', flag: '🇳🇴', esp: 'FeedBlitz' },
                            { id: 'ml_fi', label: 'MailerLite FI', flag: '🇫🇮', esp: 'MailerLite' },
                            { id: 'sl_se', label: 'SimplyLoan Direct', flag: '💎', esp: 'SimplyLoan' },
                          ]
                            .filter(e => {
                              const geo = (selectedLibCampaign?.geo || selectedGeo).toUpperCase();
                              if (geo === 'SE') return e.id.endsWith('_se') || e.id.startsWith('sl_');
                              if (geo === 'NO') return e.id.endsWith('_no');
                              if (geo === 'FI') return e.id.endsWith('_fi');
                              return true;
                            })
                            .map(e => (
                              <button
                                key={e.id}
                                onClick={() => {
                                  setPreviewEspId(e.id);
                                  renderEspPreview(e.id);
                                }}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all flex items-center gap-1 border ${
                                  previewEspId === e.id
                                    ? 'bg-indigo-50 border-indigo-500 text-indigo-700 shadow-sm ring-1 ring-indigo-300'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <span>{e.flag}</span>
                                <span>{e.label}</span>
                              </button>
                            ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => renderEspPreview(previewEspId)}
                            disabled={previewLoading}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                          >
                            <span className={previewLoading ? 'animate-spin' : ''}>🔄</span> {previewLoading ? 'Rendering...' : 'Render'}
                          </button>

                          <button
                            onClick={() => {
                              if (!libPreview) return;
                              navigator.clipboard.writeText(libPreview);
                              setLibSaveMsg('HTML Copied to Clipboard!');
                              setTimeout(() => setLibSaveMsg(''), 2000);
                            }}
                            disabled={!libPreview}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors flex items-center gap-1 disabled:opacity-40"
                          >
                            📋 Copy HTML
                          </button>

                          <button
                            onClick={async () => {
                              const updated = { ...editingVersion, status: 'ready' };
                              setEditingVersion(updated);
                              await fetch(`${API_BASE_URL}/api/text-library/version`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(updated),
                              });
                              setLibSaveMsg(`Approved ${editingVersion.versionId} for Scheduling!`);
                              setTimeout(() => setLibSaveMsg(''), 2500);
                              if (selectedLibCampaign) {
                                const r = await fetch(`${API_BASE_URL}/api/text-library/versions?campaignId=${selectedLibCampaign.campaignId}`);
                                const d = await r.json();
                                setLibVersions(d.versions || []);
                              }
                            }}
                            className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors flex items-center gap-1 shadow-sm"
                          >
                            ✅ Approve Version
                          </button>
                        </div>
                      </div>

                      {/* Live ESP Banner & Metadata */}
                      <div className="px-5 py-2 bg-slate-50 border-b border-slate-200 text-xs flex items-center justify-between text-slate-600">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-[11px] bg-slate-200/80 px-2 py-0.5 rounded text-slate-700">
                            Macros: {previewEspId.startsWith('fb_') ? '<$BlogSubscribersName$>' : previewEspId.startsWith('gr_') ? '[[name]]' : previewEspId.startsWith('dp_') ? '{{ subscriber.fname }}' : '{{ contact.FIRSTNAME }}'}
                          </span>
                          <span className="text-slate-400">·</span>
                          <span className="truncate max-w-[280px]" title={editingVersion.subject}>
                            <strong>Subject:</strong> {editingVersion.subject || '(None)'}
                          </span>
                        </div>
                        {selectedLibCampaign?.affiliateLinkSlug && (
                          <div className="text-[11px] font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                            /go/{selectedLibCampaign.affiliateLinkSlug}/
                          </div>
                        )}
                      </div>

                      {/* Interactive Iframe Canvas */}
                      <div className="flex-1 p-4 overflow-y-auto flex items-start justify-center">
                        {previewLoading ? (
                          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
                            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
                            Compiling live email template...
                          </div>
                        ) : libPreview ? (
                          <div
                            className={`bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-300 ${
                              previewDevice === 'mobile' ? 'w-[375px] max-w-[375px]' : 'w-full max-w-[700px]'
                            }`}
                            style={{ minHeight: '520px' }}
                          >
                            <iframe
                              title="ESP Live Preview"
                              srcDoc={libPreview}
                              className="w-full h-full min-h-[520px]"
                              style={{ border: 'none' }}
                              sandbox="allow-same-origin"
                            />
                          </div>
                        ) : (
                          <div className="py-24 text-center text-gray-400">
                            <div className="text-3xl mb-2">👁️</div>
                            <div className="text-sm font-medium text-gray-600">No Preview Rendered Yet</div>
                            <button
                              onClick={() => renderEspPreview(previewEspId)}
                              className="mt-3 px-4 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
                            >
                              ⚡ Render Preview Now
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* ════ TAB 2: AI WRITING ASSISTANT ════ */
                    <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-5 space-y-3" ref={el => { if (el) el.scrollTop = el.scrollHeight; }}>
                        {chatMessages.length === 0 && (
                          <div className="text-center py-8">
                            <div className="text-4xl mb-2 opacity-70">✨</div>
                            <div className="text-sm text-gray-700 font-bold">AI Copywriting Assistant</div>
                            <div className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                              Instant high-converting drafts, bullet points, and subject lines with strict independent publisher persona.
                            </div>
                            <div className="text-xs text-gray-600 mt-4 space-y-2 max-w-xs mx-auto">
                              <button
                                onClick={() => setChatInput(`Skriv ett komplett konverterande email för ${selectedLibCampaign?.advertiser || 'kampanjen'}`)}
                                className="w-full text-left px-3.5 py-2 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-900 border border-purple-200 transition-colors font-medium text-xs flex items-center gap-2 shadow-xs"
                              >
                                <span>🚀</span> Skriv komplett email-utkast
                              </button>
                              <button
                                onClick={() => setChatInput('Skriv med fokus på att samla lån och sänka månadskostnaden')}
                                className="w-full text-left px-3.5 py-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-900 border border-indigo-200 transition-colors font-medium text-xs flex items-center gap-2 shadow-xs"
                              >
                                <span>💰</span> Fokus: Samla lån & spara pengar
                              </button>
                              <button
                                onClick={() => setChatInput('Gör texten kortare, mer driven med tydligare CTA')}
                                className="w-full text-left px-3.5 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-200 transition-colors font-medium text-xs flex items-center gap-2 shadow-xs"
                              >
                                <span>⚡</span> Gör kortare & mer direkt CTA
                              </button>
                              <button
                                onClick={() => setChatInput('Föreslå 3 klickstarka och intresseväckande ämnesrader')}
                                className="w-full text-left px-3.5 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 transition-colors font-medium text-xs flex items-center gap-2 shadow-xs"
                              >
                                <span>🔥</span> 3 klickstarka ämnesrader
                              </button>
                            </div>
                          </div>
                        )}
                        {chatMessages.map((m, i) => (
                          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                              m.role === 'user'
                                ? 'bg-indigo-600 text-white rounded-br-sm'
                                : 'bg-white border border-gray-200 text-gray-700 rounded-bl-sm shadow-sm'
                            }`}>
                              {m.role === 'assistant' && m.hasUpdate && (
                                <div className="flex items-center justify-between gap-1 text-[10px] text-emerald-600 font-semibold mb-1.5 pb-1.5 border-b border-emerald-100">
                                  <span className="flex items-center gap-1"><span>✅</span> Text updated in editor</span>
                                  {m.rawText && (
                                    <button
                                      onClick={() => {
                                        const ev = { ...editingVersion };
                                        if (m.rawText) ev.textContent = m.rawText;
                                        if (m.rawSubject) ev.subject = m.rawSubject;
                                        if (m.rawPreview) ev.previewText = m.rawPreview;
                                        setEditingVersion(ev);
                                        setLibSaveMsg('Applied to editor!');
                                        setTimeout(() => setLibSaveMsg(''), 2000);
                                      }}
                                      className="text-[10px] bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded font-bold transition-colors"
                                    >
                                      📥 Re-apply
                                    </button>
                                  )}
                                </div>
                              )}
                              <div style={{ whiteSpace: 'pre-wrap' }}>{m.displayContent || m.content}</div>
                            </div>
                          </div>
                        ))}
                        {chatLoading && (
                          <div className="flex justify-start">
                            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-5 py-3 shadow-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                <span className="text-xs text-indigo-500 font-medium ml-1">Writing email copy...</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Chat Input */}
                      <div className="p-4 border-t border-gray-200 bg-white">
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          if (!chatInput.trim() || chatLoading) return;
                          const userMsg = chatInput.trim();
                          setChatInput('');
                          const newMsgs = [...chatMessages, { role: 'user' as const, content: userMsg }];
                          setChatMessages(newMsgs);
                          setChatLoading(true);
                          try {
                            const r = await fetch(`${API_BASE_URL}/api/text-library/chat`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                campaignName: selectedLibCampaign?.name || '',
                                advertiser: selectedLibCampaign?.advertiser || '',
                                geo: selectedLibCampaign?.geo || selectedGeo,
                                currentText: editingVersion?.textContent || '',
                                disclaimer: editingVersion?.disclaimer || '',
                                description: selectedLibCampaign?.description || '',
                                landingPageUrl: selectedLibCampaign?.landingPageUrl || '',
                                messages: newMsgs.map(msg => ({ role: msg.role, content: msg.content })),
                              }),
                            });
                            const d = await r.json();
                            if (d.error) {
                              setChatMessages([...newMsgs, { role: 'assistant' as const, content: '❌ ' + d.error }]);
                            } else {
                              const ev = { ...editingVersion };
                              let hasUpdate = false;
                              if (d.updatedText) { ev.textContent = d.updatedText; hasUpdate = true; }
                              if (d.updatedSubject) { ev.subject = d.updatedSubject; hasUpdate = true; }
                              if (d.updatedPreview) { ev.previewText = d.updatedPreview; hasUpdate = true; }
                              if (hasUpdate) setEditingVersion(ev);
                              setChatMessages([...newMsgs, {
                                role: 'assistant' as const,
                                content: d.reply || 'Done.',
                                displayContent: d.reply || 'Done.',
                                hasUpdate,
                                rawText: d.updatedText,
                                rawSubject: d.updatedSubject,
                                rawPreview: d.updatedPreview,
                              }]);
                            }
                          } catch (err) {
                            setChatMessages([...newMsgs, { role: 'assistant' as const, content: '❌ Network error' }]);
                          }
                          setChatLoading(false);
                        }} className="flex gap-2">
                          <input
                            type="text"
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            placeholder="Fråga AI att skriva, förkorta, ändra vinkel..."
                            className="flex-1 text-sm rounded-full px-5 py-2.5 border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition-all"
                            disabled={chatLoading}
                          />
                          <button
                            type="submit"
                            disabled={chatLoading || !chatInput.trim()}
                            className="w-10 h-10 flex items-center justify-center rounded-full text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-30 transition-colors shadow-md"
                          >
                            ↑
                          </button>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <style>{`@keyframes modalSlideUp { from { opacity: 0; transform: translateY(24px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
